import React, { useEffect, useState, useCallback } from "react";
import { Card, CardBody, Col, Container, Row, Modal, ModalBody } from "reactstrap";
import BreadCrumb from "../Common/BreadCrumb";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "../Utils/Loader";
import { customerIdData } from "../Customers/constants/constantFunctionsView";
import { getUserByName } from "../../slices/UserManagement/GetUserByName/thunk";
import logoLarge from '../../assets/images/users/user-dummy-img.jpg';
import { viewDoc } from "../../slices/customer/documentview/thunk";
import { resetDocumentViewState } from "../../slices/customer/documentview/reducer";
import CommonButton from "../Utils/CommonButton";
import CreateEditUser from "./CreateEditUser";
import { setEditSuccessMessage, setEmailUpdateSuccessMessage } from "../../slices/toastMessage/action";
import StatusBadge from "../Customers/StatusBadge";

export default function ViewUser() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const operatorId = localStorage.getItem('operatorId');
    const [loading, setLoading] = useState(false);
    const isEdit = location.pathname.includes('view-user');
    const userId = customerIdData();
    const [profileImage, setProfileImage] = useState(logoLarge);
    const [editData, setEditData] = useState();
    const [profileData, setProfileData] = useState(false);
    const [userDetailsModal, setUserDetailsModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [formValues, setFormValues] = useState({});
    const [formErrors, setFormErrors] = useState({});
    const [formTouched, setFormTouched] = useState({});
    const [fileSelected, setFileSelected] = useState(false);
    const [imageOn, setImageOn] = useState(false);
    const [logoFile, setLogoFile] = useState(null);

    const initialValues = {
        firstname: "",
        lastname: "",
        username: "",
        userrole: "",
        phone: "",
        email: "",
        status: ""
    };
    const operatorDomain = location?.state?.domain

    // Helper function to close modal and reset state
    const closeModal = useCallback(() => {
        setUserDetailsModal(false);
        setSelectedUser(null);
        setIsEditMode(false);
        document.body.style.overflow = 'auto';
        // Reset form state
        setFormValues({});
        setFormErrors({});
        setFormTouched({});
        setFileSelected(false);
        setImageOn(false);
        setLogoFile(null);
    }, []);

    const handleOperationSuccess = useCallback((type) => {
        closeModal();
        // Refresh user list if needed
    }, [closeModal, dispatch, operatorId]);

    const showEditSuccessMessage = useSelector((state) => state.SuccessToast.showEditSuccessMessage);
    const showEmailUpdateSuccessMessage = useSelector((state) => state.SuccessToast.showEmailUpdateSuccessMessage);

    useEffect(() => {
        if (isEdit) {
            dispatch(getUserByName(userId, setLoading, initialValues, setEditData));
        }
    }, [dispatch]);

    const user = useSelector((state) => state.GetAllUserByName.getUsersByName);

    useEffect(() => {
        if (editData && editData?.userDetails?.userName) {
            const partnerOrCustomer = "User";
            dispatch(viewDoc(setLoading, userId, "logo", partnerOrCustomer, setProfileData));
        }
    }, [dispatch, editData]);

    const ViewDocument = useSelector(state => state.ViewDocument.documentView);
    const base64ImageData = ViewDocument?.filePath;

    useEffect(() => {
        setProfileImage(base64ImageData || logoLarge);
    }, [base64ImageData]);

    useEffect(() => {
        if (showEditSuccessMessage) {
            toast.success(`User Updated Successfully`);
            dispatch(setEditSuccessMessage(false));
            dispatch(getUserByName(userId, setLoading, initialValues, setEditData));
            // Close modal after showing success message
            closeModal();
        }
    }, [showEditSuccessMessage, dispatch, userId, closeModal]);

    useEffect(() => {
        if (showEmailUpdateSuccessMessage) {
            toast.success(`Verification link has been sent to your updated email`);
            dispatch(setEmailUpdateSuccessMessage(false));
            // Close modal after showing success message
            closeModal();
        }
    }, [showEmailUpdateSuccessMessage, dispatch, closeModal]);

    const toggleUserDetailsModal = () => {
        setUserDetailsModal(!userDetailsModal);
        if (!userDetailsModal) {
            setFormValues({});
            setFormErrors({});
            setFormTouched({});
            setFileSelected(false);
            setImageOn(false);
            setLogoFile(null);
            setSelectedUser(user);
            setIsEditMode(true);
        } else {
            setSelectedUser(null);
            setIsEditMode(false);
        }
        document.body.style.overflow = userDetailsModal ? 'auto' : 'hidden';
    };

    const handleEditUser = () => {
        setIsEditMode(true);
        setSelectedUser(user);
        toggleUserDetailsModal();
    };

    const renderDoubleColumnRow = (leftLabel, leftValue, rightLabel, rightValue, noBorder = false) => {
        const formatValue = (label, value) => {
            if (!label) return '';
            return value ? value : 'N/A';
        };

        return (
            <div className={`d-flex mb-3 pb-2 ${noBorder ? '' : 'border-bottom'}`}>
                <div className="w-50 pe-3">
                    {leftLabel && <label className="fw-semibold">{leftLabel}</label>}
                    <p className="detail-value mb-0">{formatValue(leftLabel, leftValue)}</p>
                </div>
                <div className="w-50 ps-3">
                    {rightLabel && <label className="fw-semibold">{rightLabel}</label>}
                    <p className="detail-value mb-0">{formatValue(rightLabel, rightValue)}</p>
                </div>
            </div>
        );
    };

    const renderSingleColumnRow = (label, value, noBorder = false) => {
        const formatValue = (value) => {
            return value ? value : 'N/A';
        };

        return (
            <div className={`mb-3 pb-2 ${noBorder ? '' : 'border-bottom'}`}>
                <label className="fw-semibold">{label}</label>
                <p className="detail-value mb-0">{formatValue(value)}</p>
            </div>
        );
    };

    return (
        <React.Fragment>
            <div id='csms' className='page-content bg-white'>
                <Container fluid>
                    <BreadCrumb title={"User"} pageTitle={"Users"} />
                    <ToastContainer position='top-center' />

                    <div className="user-profile-container py-2">
                        <Row className="justify-content-center">
                            <Col lg={10}>
                                <div className="rounded-1 px-3 py-3 my-2 bg-white">
                                    <CardBody>
                                        <div className="d-flex align-items-center mb-4">
                                            <div className="profile-image-container me-4">
                                                <img
                                                    src={profileImage}
                                                    className="rounded-circle avatar-xl img-thumbnail user-profile-image"
                                                    alt="user-profile"
                                                    style={{ width: '110px', height: '110px', objectFit: 'cover' }}
                                                />
                                            </div>
                                            <div>
                                                <h4 className="mb-1 fw-bold text-black">{user?.userDetails?.userName || 'N/A'}</h4>
                                                <p className="text-muted mb-0">{userId || 'N/A'}</p>
                                                <div className="mt-1">
                                                    <StatusBadge value={user?.status?.charAt(0).toUpperCase() + user?.status?.slice(1).toLowerCase() || 'Inactive'} />
                                                </div>
                                            </div>
                                            <div className="ms-auto mt-n5">
                                                <CommonButton
                                                    color={"main-color"}
                                                    className={
                                                        "btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap mt-n4"
                                                    }
                                                    icon={
                                                        <i className="bx bxs-edit label-icon align-middle fs-16 me-1"></i>
                                                    }
                                                    btnTxtClsName={"px-2 ps-1 ms-3 me-1"}
                                                    type={"submit"}
                                                    buttonAction={handleEditUser}
                                                    buttonText={'Edit'}
                                                />
                                            </div>
                                        </div>
                                        <div className="profile-details-section">
                                            <p className="fs-5 fw-semibold mb-3 pb-2 border-bottom">User Details</p>
                                            {renderDoubleColumnRow("User ID", userId, "User Name", user?.userDetails?.userName)}
                                            {renderDoubleColumnRow("User Role", user?.role, "Email Address", user?.email)}
                                            {renderSingleColumnRow("Phone Number", `${user?.userDetails?.dialCode || ''} ${user?.userDetails?.phone || ''}`, true)}
                                        </div>
                                    </CardBody>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Container>
                {loading && <Loader />}
            </div>

            {/* Edit User Modal */}
            <Modal isOpen={userDetailsModal} toggle={toggleUserDetailsModal} centered size="lg" className="custom-modal">
                <button type="button" className="close-button-currency mt-n1" onClick={toggleUserDetailsModal}>
                    &times;
                </button>
                <h5 className="text-center w-100 fw-semibold py-3 rv-breadcrumb-border">
                    Edit User
                </h5>
                <ModalBody>
                    <CreateEditUser
                        isEditMode={isEditMode}
                        userInformation={user}
                        onCancel={toggleUserDetailsModal}
                        onSuccess={handleOperationSuccess}
                        operatorUser={operatorDomain ? operatorDomain : false}
                        customRoles={[]}
                    />
                </ModalBody>
            </Modal>
        </React.Fragment>
    );
}